import React, { useState, useEffect, useCallback } from 'react';
import {
  Paper,
  Typography,
  Box,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  CircularProgress,
  Alert,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  LinearProgress
} from '@mui/material';
import {
  CloudUpload,
  Download,
  Delete,
  InsertDriveFile,
  Image,
  PictureAsPdf,
  VideoFile,
  AudioFile,
  Archive
} from '@mui/icons-material';
import { useTeacher } from '../contexts/TeacherContext';
import dayjs from 'dayjs';
import 'dayjs/locale/it';

const CourseFiles = ({ courseId, isTeacher, isCourseTeacher, isAdmin }) => {
  const { getCourseFiles, uploadCourseFile, deleteCourseFile, downloadCourseFile } = useTeacher();
  const [files, setFiles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [error, setError] = useState(null);
  const [deleteDialog, setDeleteDialog] = useState({ open: false, file: null });
  const [deleting, setDeleting] = useState(false);

  const loadFiles = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
    //   clearError();
      
      const result = await getCourseFiles(courseId);
      if (result.success) {
        setFiles(result.data.data || []);
      } else {
        setError(result.error || 'Failed to load files');
      }
    } catch (err) {
      console.error('Error loading files:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [courseId, getCourseFiles]);

  // Load files on component mount
  useEffect(() => {
    loadFiles();
  }, [loadFiles]);


  const handleFileUpload = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    // Validate file size (10MB limit)
    if (file.size > 10 * 1024 * 1024) {
      setError('File size must be less than 10MB');
      return;
    }

    // Validate file type
    const allowedTypes = [
      'image/jpeg',
      'image/png',
      'image/gif',
      'application/pdf',
      'text/plain',
      'application/msword',
      'application/vnd.openxmlformats-officedocument.wordprocessingml.document'
    ];

    if (!allowedTypes.includes(file.type)) {
      setError('File type not allowed');
      return;
    }

    try {
      setUploading(true);
      setUploadProgress(0);
      setError(null);
    //   clearError();

      // Simulate upload progress
      const progressInterval = setInterval(() => {
        setUploadProgress(prev => Math.min(prev + 10, 90));
      }, 100);

      const result = await uploadCourseFile(courseId, file);

      clearInterval(progressInterval);
      setUploadProgress(100);

      if (result.success) {
        // Add the new file to the list
        setFiles(prev => {
          const currentFiles = Array.isArray(prev) ? prev : [];
          // console.log('result', result);
          return [result.data.data, ...currentFiles];
        });
        setUploadProgress(0);
      } else {
        setError(result.error || 'Upload failed');
      }
    } catch (err) {
      console.error('Upload error:', err);
      setError(err.message);
    } finally {
      setUploading(false);
    }
  };

  const handleDownload = async (file) => {
    try {
      setError(null);
    //   clearError();
      
      const result = await downloadCourseFile(courseId, file.key);
      if (!result.success) {
        setError(result.error || 'Download failed');
      }
    } catch (err) {
      console.error('Download error:', err);
      setError(err.message);
    }
  };

  const handleDeleteClick = (file) => {
    setDeleteDialog({ open: true, file });
  };

  const handleDeleteConfirm = async () => {
    if (!deleteDialog.file) return;

    try {
      setDeleting(true);
      setError(null);
    //   clearError();
      
      const result = await deleteCourseFile(courseId, deleteDialog.file.key);
      
      if (result.success) {
        // Remove file from list
        setFiles(prev => {
          const currentFiles = Array.isArray(prev) ? prev : [];
          return currentFiles.filter(f => f.key !== deleteDialog.file.key);
        });
        setDeleteDialog({ open: false, file: null });
      } else {
        setError(result.error || 'Delete failed');
      }
    } catch (err) {
      console.error('Delete error:', err);
      setError(err.message);
    } finally {
      setDeleting(false);
    }
  };

  const getFileIcon = (contentType) => {
    if (!contentType) return <InsertDriveFile />;
    if (contentType.startsWith('image/')) return <Image />;
    if (contentType === 'application/pdf') return <PictureAsPdf />;
    if (contentType.startsWith('video/')) return <VideoFile />;
    if (contentType.startsWith('audio/')) return <AudioFile />;
    if (contentType.includes('zip') || contentType.includes('rar')) return <Archive />;
    return <InsertDriveFile />;
  };

  const formatFileSize = (bytes) => {
    if (!bytes || bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  const canDelete = isAdmin || (isTeacher && isCourseTeacher);


  if (loading) {
    return (
      <Paper sx={{ p: 3, mb: 2 }}>
        <Box display="flex" justifyContent="center" alignItems="center" minHeight="100px">
          <CircularProgress />
        </Box>
      </Paper>
    );
  }

  return (
    <Paper sx={{ p: 3, mb: 2 }}>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
        <Typography variant="h6">
          Materiali del Corso
        </Typography>
        {(isAdmin || (isTeacher && isCourseTeacher)) && (
          <Button
            variant="contained"
            startIcon={<CloudUpload />}
            component="label"
            disabled={uploading}
          >
            {uploading ? 'Caricamento...' : 'Carica File'}
            <input
              type="file"
              hidden
              onChange={handleFileUpload}
              accept=".jpg,.jpeg,.png,.gif,.pdf,.txt,.doc,.docx"
            />
          </Button>
        )}
      </Box>

      {(error) && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => { setError(null); }}>
          {error}
        </Alert>
      )}

      {uploading && (
        <Box sx={{ mb: 2 }}>
          <Typography variant="body2" gutterBottom>
            Caricamento in corso...
          </Typography>
          <LinearProgress variant="determinate" value={uploadProgress} />
        </Box>
      )}

      {(!Array.isArray(files) || files.length === 0) ? (
        <Box sx={{ textAlign: 'center', py: 4, color: 'text.secondary' }}>
          <InsertDriveFile sx={{ fontSize: 48, mb: 1, opacity: 0.5 }} />
          <Typography variant="body1">
            Nessun materiale caricato per questo corso
          </Typography>
        </Box>
      ) : (
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Nome File</TableCell>
                <TableCell>Dimensione</TableCell>
                <TableCell>Caricato da</TableCell>
                <TableCell>Data Caricamento</TableCell>
                <TableCell align="center">Azioni</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
               {files.map((file) => (
                  <TableRow key={file.key} hover>
                  <TableCell>
                    <Box display="flex" alignItems="center" gap={1}>
                      {getFileIcon(file.contentType)}
                      <Typography variant="body2" noWrap>
                        {file.fileName}
                      </Typography>
                    </Box>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2">
                      {formatFileSize(file.size)}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2">
                      {file.uploadedBy || 'Sconosciuto'}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2">
                      {(file.uploadDate || file.uploadedAt || file.lastModified) ? 
                        dayjs(file.uploadDate || file.uploadedAt || file.lastModified).locale('it').format('DD/MM/YYYY HH:mm') : 
                        'N/A'
                      }
                    </Typography>
                  </TableCell>
                  <TableCell align="center">
                    <Box display="flex" gap={1} justifyContent="center">
                      <Tooltip title="Scarica">
                        <IconButton
                          size="small"
                          color="primary"
                          onClick={() => handleDownload(file)}
                        >
                          <Download />
                        </IconButton>
                      </Tooltip>
                      {canDelete && (
                        <Tooltip title="Elimina">
                          <IconButton
                            size="small"
                            color="error"
                            onClick={() => handleDeleteClick(file)}
                          >
                            <Delete />
                          </IconButton>
                        </Tooltip>
                      )}
                    </Box>
                  </TableCell>
                </TableRow>
               ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={deleteDialog.open}
        onClose={() => setDeleteDialog({ open: false, file: null })}
      >
        <DialogTitle>Conferma Eliminazione</DialogTitle>
        <DialogContent>
          <Typography>
            Sei sicuro di voler eliminare il file "{deleteDialog.file?.fileName}"?
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button 
            onClick={() => setDeleteDialog({ open: false, file: null })}
            disabled={deleting}
          >
            Annulla
          </Button>
          <Button
            onClick={handleDeleteConfirm}
            color="error"
            variant="contained"
            disabled={deleting}
            startIcon={deleting ? <CircularProgress size={16} /> : <Delete />}
          >
            {deleting ? 'Eliminazione...' : 'Elimina'}
          </Button>
        </DialogActions>
      </Dialog>
    </Paper>
  );
};

export default CourseFiles;
